import { MakersBoard } from "@/components/makers/MakersBoard";
import { RolesContext } from "@/contexts/RolesContext";
import {
  OooContextBridge,
  type OooContextValue,
} from "@/contexts/OooContext";
import { useMakersBoardData } from "@/hooks/useMakersBoardData";
import { type ModalActions } from "@/hooks/useModal";
import type {
  Assignment,
  AssignmentEmployee,
  ProjectSummary,
} from "@/hooks/useProjectBoardData";
import type { UseRolesResult } from "@/hooks/useRoles";
import {
  createAssignment,
  deleteAssignment,
  employeeSummaryToAssignmentEmployee,
  saveAssignmentEmployees,
} from "@/utils/assignmentEmployees";
import { withWrittenEmployees } from "@/utils/assignmentConfirmation";
import { assignmentAuditLabel, projectAuditLabel } from "@/utils/auditLabels";
import { addWeeks, parseISODate } from "@/utils/dateUtils";
import {
  activeTabs,
  assignedElsewhereIds,
  buildBoardUpdate,
  buildSavePlan,
  hasPendingChanges,
  isSelectionDirty,
  nextActiveId,
  selectionFor,
  type EmployeeBoardUpdate,
  type PendingState,
} from "@/utils/findPeoplePending";
import { hasAvailabilityForDateRange, type AvailabilityMode } from "@/utils/makersUtils";
import { formatEmployeeDisplayName } from "@/utils/nameUtils";
import { Plus, Undo2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type MakersData = ReturnType<typeof useMakersBoardData>;

/** Resolves a role id to its display name (from useRoles().formatRole). */
type FormatRole = (id: string | null | undefined) => string;

interface FindPeopleModalProps extends ModalActions {
  project: ProjectSummary;
  /** The role tab to open on (the one whose "Find People" button was clicked). */
  initialAssignmentId: string;
  /** Applies all role assignment changes in one optimistic update (avoids
   * clobbering when several roles are saved together). */
  assignEmployeesOptimistically: (updates: EmployeeBoardUpdate[]) => void;
  refreshSilently: () => Promise<void>;
  /** Optimistically patch the project on the board (used when adding/removing roles). */
  updateProjectOptimistically: (
    projectId: string,
    updates: Partial<ProjectSummary>,
  ) => void;
  selectedOffices?: string[];
  /** The user's saved availability-mode default. Threaded in because the modal
   * renders in a separate React root without access to UserSettingsContext. */
  defaultAvailabilityMode?: AvailabilityMode;
  // The whole useRoles() result is bridged in as a prop: the modal renders in a
  // detached React root (see useModal) outside the app's RolesProvider, so
  // useRoles() here — and anywhere in the modal subtree (e.g. MakersBoard's role
  // filter) — would only see the empty default context. We re-provide this value
  // via RolesContext below so the entire subtree resolves roles correctly.
  rolesValue: UseRolesResult;
  /** The modal has its own React root, so bridge the project board's live OOO data. */
  oooValue: OooContextValue;
  /**
   * The board's self-edit marker. Like roles and OOO, it has to be passed in:
   * useModal renders this modal in a separate React root, outside the
   * RecentlyChangedRows provider, where the context hook returns a no-op. Without
   * the real one, echoes of this modal's own writes look like remote edits and
   * trigger board refetches that can overwrite later optimistic updates.
   */
  markSelfEdit: (table: string, id: string, ttlMs?: number) => void;
}

function assignmentRoleLabel(
  assignment: Assignment,
  formatRole: FormatRole,
): string {
  return formatRole(assignment.required_role_id) || "Role TBD";
}

// One tab label per role. The label reflects who is currently selected for that
// role (live, including unsaved picks); with no one selected it's just the role
// name, disambiguated by a number when the same role appears more than once.
function buildTabLabels(
  assignments: Assignment[],
  selectedNamesFor: (a: Assignment) => string[],
  formatRole: FormatRole,
): string[] {
  const roleCounts = new Map<string, number>();
  for (const a of assignments) {
    const role = assignmentRoleLabel(a, formatRole);
    roleCounts.set(role, (roleCounts.get(role) ?? 0) + 1);
  }
  const used = new Map<string, number>();
  return assignments.map((a) => {
    const role = assignmentRoleLabel(a, formatRole);
    const names = selectedNamesFor(a);
    if (names.length > 0) return `${role} · ${names.join(", ")}`;
    if ((roleCounts.get(role) ?? 0) <= 1) return role;
    const n = (used.get(role) ?? 0) + 1;
    used.set(role, n);
    return `${role} (${n})`;
  });
}

/**
 * Modal for finding people to assign to a project's roles. Shows one tab per role
 * so the user can move between roles and assign without reopening the modal; opens
 * on the role whose "Find People" button was clicked. Selections persist across
 * tabs (nothing is discarded by switching), and a single "Save All" commits every
 * role that changed.
 *
 * Role structure is part of that batch too: adding a role creates a tab locally and
 * removing one marks its tab struck-through, but neither touches the database or the
 * board until Save All — so Cancel genuinely cancels. The modal stays open while the
 * writes run (inserts, then employee attachments, then deletes) and reports a failure
 * in place rather than closing over it.
 */
export function FindPeopleModal({
  project,
  initialAssignmentId,
  close,
  assignEmployeesOptimistically,
  refreshSilently,
  updateProjectOptimistically,
  selectedOffices = [],
  defaultAvailabilityMode,
  rolesValue,
  oooValue,
  markSelfEdit,
}: FindPeopleModalProps) {
  const { roles, formatRole } = rolesValue;
  const makersData = useMakersBoardData();
  // Roles live in local state so tabs added/removed inside the modal update
  // immediately (the `project` prop is a snapshot captured when the modal opened
  // and doesn't react to the parent's optimistic updates).
  const [assignments, setAssignments] = useState<Assignment[]>(
    project.project_assignments,
  );
  const [activeAssignmentId, setActiveAssignmentId] =
    useState(initialAssignmentId);
  const [showRolePicker, setShowRolePicker] = useState(false);
  // Roles added in this session: present in `assignments` but not in the database
  // until save. Their ids are minted here so the insert and the employee attach
  // at save time can share a known id.
  const [pendingAddIds, setPendingAddIds] = useState<Set<string>>(new Set());
  // Saved roles queued for deletion. Their tabs stay visible, struck through, so
  // the pending state is legible and "Cancel discards this" is discoverable.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Pending selection per role, keyed by assignment id. A role is absent until the
  // user touches it; absent means "unchanged from its currently-saved employees".
  const [selections, setSelections] = useState<Map<string, Set<string>>>(
    new Map(),
  );

  // Opening the modal for a specific role focuses that role's tab.
  useEffect(() => {
    setActiveAssignmentId(initialAssignmentId);
  }, [initialAssignmentId]);

  // Resolve employee id → display name from the full list and the saved assignments.
  const employeeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of assignments) {
      for (const e of a.employees) {
        const name = formatEmployeeDisplayName(e);
        if (name) map.set(e.id, name);
      }
    }
    for (const e of makersData.employees) {
      const name = formatEmployeeDisplayName(e);
      if (name) map.set(e.id, name);
    }
    return map;
  }, [assignments, makersData.employees]);

  const pendingState: PendingState = {
    assignments,
    pendingAddIds,
    pendingDeleteIds,
    selections,
  };
  const liveTabs = activeTabs(pendingState);
  // Focus can't rest on a tab queued for deletion; with none left the panel below
  // falls through to the empty state.
  const activeAssignment =
    liveTabs.find((a) => a.id === nextActiveId(pendingState, activeAssignmentId)) ?? null;

  // People already on the project through another role shouldn't be selectable
  // here (no double-booking one person across two roles); the active role's own
  // people stay visible so they can still be reviewed or removed. Keyed by a stable
  // string so the Set identity only changes when membership does — otherwise the
  // board would treat every render as a data change and re-pin selected rows to top.
  const excludeKey = assignedElsewhereIds(
    pendingState,
    activeAssignment?.id ?? null,
  ).join(",");
  const assignedElsewhere = useMemo(
    () => new Set(excludeKey ? excludeKey.split(",") : []),
    [excludeKey],
  );

  const selectedNamesFor = (a: Assignment): string[] => {
    const names: string[] = [];
    for (const id of selectionFor(a, selections)) {
      const name = employeeNameById.get(id);
      if (name) names.push(name);
    }
    return names;
  };

  const tabLabels = buildTabLabels(assignments, selectedNamesFor, formatRole);

  // Structural changes count on their own, so Save All is enabled when the only
  // pending change is an added or a deleted role.
  const anyDirty = hasPendingChanges(pendingState);

  const toggleEmployee = (employeeId: string) => {
    if (!activeAssignment) return;
    setSelections((prev) => {
      const next = new Map(prev);
      const current = new Set(selectionFor(activeAssignment, selections));
      if (current.has(employeeId)) current.delete(employeeId);
      else current.add(employeeId);
      next.set(activeAssignment.id, current);
      return next;
    });
  };

  const auditContextFor = (assignment: Assignment) => ({
    recordLabel: assignmentAuditLabel(
      projectAuditLabel(project),
      assignment,
      formatRole,
    ),
    officeId: project.office_id ?? null,
    projectStartDate: project.start_date,
  });

  // Add a role tab. Local only: nothing reaches the database or the board until
  // Save All, so the id is minted here and reused for the insert at save time.
  // display_order is taken now — gaps are already possible from ordinary deletes
  // and nothing downstream depends on the sequence being dense.
  const handleAddRole = (roleId: string) => {
    setShowRolePicker(false);

    const numWeeks = project.num_weeks ?? 1;
    const assignmentId = crypto.randomUUID();
    const nextDisplayOrder =
      assignments.reduce((max, a) => Math.max(max, a.display_order ?? 0), -1) + 1;

    const newAssignment: Assignment = {
      id: assignmentId,
      project_id: project.id,
      required_role_id: roleId,
      weekly_hours_allocated: Array.from({ length: numWeeks }, () => 0),
      is_confirmed: false,
      notes: null,
      display_order: nextDisplayOrder,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      employees: [],
    };

    setAssignments((prev) => [...prev, newAssignment]);
    setPendingAddIds((prev) => new Set(prev).add(assignmentId));
    setActiveAssignmentId(assignmentId);
  };

  /**
   * The tab's X. A saved role is queued for deletion (and un-queued by pressing it
   * again); a role added in this session has nothing to delete, so it just goes —
   * a tombstone for a row the database never heard of would only muddy the
   * pending model.
   */
  const toggleDeleteRole = (assignment: Assignment) => {
    if (pendingAddIds.has(assignment.id)) {
      setAssignments((prev) => prev.filter((a) => a.id !== assignment.id));
      setPendingAddIds((prev) => {
        const next = new Set(prev);
        next.delete(assignment.id);
        return next;
      });
      setSelections((prev) => {
        if (!prev.has(assignment.id)) return prev;
        const next = new Map(prev);
        next.delete(assignment.id);
        return next;
      });
      return;
    }

    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      if (next.has(assignment.id)) next.delete(assignment.id);
      else next.add(assignment.id);
      return next;
    });
  };

  /**
   * Commit the whole batch in order: inserts, then employee attachments, then
   * deletes. The modal stays open until it's done — a role the user believes they
   * created but that failed to insert is not something to close over.
   *
   * On a partial failure only the operations that actually committed are cleared,
   * so pressing Save again retries the rest instead of double-applying.
   */
  const handleSaveAll = async () => {
    if (saving) return;
    setSaveError(null);
    setSaving(true);

    const plan = buildSavePlan(pendingState);
    const committedAdds = new Set<string>();
    const committedDeletes = new Set<string>();
    const committedSelections = new Map<string, Set<string>>();
    // is_confirmed as each employee save returned it, so the board shows the
    // database's lock rather than this modal's possibly stale copy.
    const persistedConfirmed = new Map<string, boolean | null>();
    const failedInserts = new Set<string>();
    const failures: string[] = [];

    for (const assignment of plan.inserts) {
      const { error } = await createAssignment(
        {
          id: assignment.id,
          project_id: project.id,
          required_role_id: assignment.required_role_id,
          weekly_hours_allocated: assignment.weekly_hours_allocated,
          is_confirmed: false,
          display_order: assignment.display_order,
        },
        markSelfEdit,
        auditContextFor(assignment),
      );
      if (error) {
        failures.push(error);
        failedInserts.add(assignment.id);
      } else {
        committedAdds.add(assignment.id);
      }
    }

    for (const { assignment, selectedIds } of plan.employeeWrites) {
      // Never attach people to a row that failed to insert.
      if (failedInserts.has(assignment.id)) continue;
      const writeErrors: string[] = [];
      const saved = await saveAssignmentEmployees(
        assignment,
        selectedIds,
        markSelfEdit,
        () => {},
        (message) => {
          writeErrors.push(message);
        },
        {
          recordLabel: assignmentAuditLabel(
            projectAuditLabel(project),
            assignment,
            formatRole,
          ),
          nameById: employeeNameById,
          officeId: project.office_id ?? null,
        },
      );
      if (writeErrors.length > 0) failures.push(...writeErrors);
      else committedSelections.set(assignment.id, selectedIds);
      if (saved && saved.isConfirmed !== null) {
        persistedConfirmed.set(assignment.id, saved.isConfirmed);
      }
    }

    for (const assignment of plan.deletes) {
      const { error } = await deleteAssignment(
        assignment,
        assignmentRoleLabel(assignment, formatRole),
        markSelfEdit,
        auditContextFor(assignment),
      );
      if (error) failures.push(error);
      else committedDeletes.add(assignment.id);
    }

    if (failures.length > 0) {
      // Committed employee writes become the new baseline, so a retry's final
      // board update isn't built from the pre-save assignees.
      setAssignments((prev) =>
        prev
          .filter((a) => !committedDeletes.has(a.id))
          .map((a) => {
            const ids = committedSelections.get(a.id);
            return ids
              ? withWrittenEmployees(
                  a,
                  buildAssignmentEmployees(ids, makersData.employees),
                  persistedConfirmed.get(a.id),
                )
              : a;
          }),
      );
      setPendingAddIds((prev) => withoutIds(prev, committedAdds));
      setPendingDeleteIds((prev) => withoutIds(prev, committedDeletes));
      setSelections((prev) => {
        if (committedSelections.size === 0) return prev;
        const next = new Map(prev);
        for (const id of committedSelections.keys()) next.delete(id);
        return next;
      });
      setSaveError(failures[0] ?? "Failed to save changes");
      setSaving(false);
      await refreshSilently();
      return;
    }

    // Everything landed, so the board can be patched from what we just wrote.
    const writtenEmployees = new Map(
      plan.employeeWrites.map(({ assignment, selectedIds }) => [
        assignment.id,
        buildAssignmentEmployees(selectedIds, makersData.employees),
      ]),
    );
    // Exactly one patch: a structural update already carries the written
    // assignees, and the employee-only updater rebuilds from the stale snapshot.
    const boardUpdate = buildBoardUpdate(
      pendingState,
      plan,
      writtenEmployees,
      persistedConfirmed,
    );
    if (boardUpdate?.kind === "structure") {
      updateProjectOptimistically(project.id, {
        project_assignments: boardUpdate.projectAssignments,
      });
    } else if (boardUpdate?.kind === "employees") {
      assignEmployeesOptimistically(boardUpdate.updates);
    }

    setSaving(false);
    close();
  };

  return (
    <RolesContext.Provider value={rolesValue}>
      <OooContextBridge value={oooValue}>
        {saveError && (
          <div className="error-banner">
            <span className="error-banner__message">{saveError}</span>
          </div>
        )}
        <div className="find-people-tabs" role="tablist">
          {assignments.map((a, i) => {
            const isActive = activeAssignment?.id === a.id;
            const pendingDelete = pendingDeleteIds.has(a.id);
            const roleLabel = assignmentRoleLabel(a, formatRole);
            const unsaved = pendingAddIds.has(a.id) || isSelectionDirty(a, selections);
            return (
              <div
                key={a.id}
                className={`find-people-tab${isActive ? " find-people-tab--active" : ""}${pendingDelete ? " find-people-tab--pending-delete" : ""}`}
                data-testid="find-people-tab"
                data-assignment-id={a.id}
                data-pending-delete={pendingDelete}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className="find-people-tab__label"
                  disabled={pendingDelete}
                  onClick={() => setActiveAssignmentId(a.id)}
                >
                  {tabLabels[i]}
                  {unsaved && !pendingDelete && (
                    <span
                      className="find-people-tab__dirty"
                      aria-label="unsaved changes"
                    />
                  )}
                </button>
                <button
                  type="button"
                  className="find-people-tab__close"
                  aria-label={
                    pendingDelete
                      ? `Undo remove ${roleLabel} role`
                      : `Remove ${roleLabel} role`
                  }
                  title={pendingDelete ? "Undo remove role" : "Remove role"}
                  disabled={saving}
                  onClick={() => toggleDeleteRole(a)}
                >
                  {pendingDelete ? <Undo2 size={13} /> : <X size={13} />}
                </button>
              </div>
            );
          })}
          {showRolePicker ? (
            <select
              className="find-people-tab__add-select"
              data-testid="find-people-add-role-select"
              value=""
              autoFocus
              disabled={saving}
              aria-label="Add role"
              onChange={(e) => {
                const roleId = e.target.value;
                if (roleId) handleAddRole(roleId);
              }}
              onBlur={() => setShowRolePicker(false)}
            >
              <option value="" disabled>
                Select a role…
              </option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.display_name}
                </option>
              ))}
            </select>
          ) : (
            <button
              type="button"
              className="find-people-tab__add"
              aria-label="Add role"
              title="Add role"
              disabled={saving}
              onClick={() => setShowRolePicker(true)}
            >
              <Plus size={16} />
            </button>
          )}
        </div>
        {activeAssignment ? (
          <FindPeopleTabPanel
            // Remount per role so the board view (search/availability filter) resets to
            // that role. Selections live in the parent, so they persist across tabs.
            key={activeAssignment.id}
            assignment={activeAssignment}
            project={project}
            makersData={makersData}
            selectedOffices={selectedOffices}
            defaultAvailabilityMode={defaultAvailabilityMode}
            selectedEmployeeIds={selectionFor(activeAssignment, selections)}
            excludeEmployeeIds={assignedElsewhere}
            onToggleEmployee={toggleEmployee}
          />
        ) : (
          <div className="find-people-empty">
            {assignments.length === 0
              ? "No roles yet. Use the + button to add one."
              : "No roles selected. Undo a removal above, or add a role."}
          </div>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={close}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            data-testid="save-assignments-button"
            onClick={handleSaveAll}
            disabled={!anyDirty || saving}
          >
            {saving ? "Saving..." : "Save All"}
          </button>
        </div>
      </OooContextBridge>
    </RolesContext.Provider>
  );
}

/** `source` minus `remove`, returning the original set when nothing is dropped. */
function withoutIds(source: Set<string>, remove: Set<string>): Set<string> {
  if (remove.size === 0) return source;
  const next = new Set(source);
  for (const id of remove) next.delete(id);
  return next;
}

type MakerEmployee = MakersData["employees"][number];

function buildAssignmentEmployees(
  selectedEmployeeIds: Set<string>,
  employees: MakerEmployee[],
): AssignmentEmployee[] {
  const result: AssignmentEmployee[] = [];
  for (const employeeId of selectedEmployeeIds) {
    const employee = employees.find((emp) => emp.id === employeeId);
    if (employee) {
      result.push(employeeSummaryToAssignmentEmployee(employee));
    }
  }
  return result;
}

/**
 * The find-people board for a single role tab. Selection is controlled by the
 * parent so a tab's changes survive switching to another tab.
 */
function FindPeopleTabPanel({
  assignment,
  project,
  makersData,
  selectedEmployeeIds,
  excludeEmployeeIds,
  onToggleEmployee,
  selectedOffices = [],
  defaultAvailabilityMode,
}: {
  assignment: Assignment;
  project: ProjectSummary;
  makersData: MakersData;
  selectedEmployeeIds: Set<string>;
  excludeEmployeeIds: Set<string>;
  onToggleEmployee: (employeeId: string) => void;
  selectedOffices?: string[];
  defaultAvailabilityMode?: AvailabilityMode;
}) {
  const { employees, loading, error, refresh } = makersData;
  const [filterByAvailability, setFilterByAvailability] =
    useState<boolean>(false);

  // The panel co-owns the availability mode with MakersBoard's toggle so the
  // candidate filter below (hasAvailabilityForDateRange) and the availability the
  // board displays use the same calculation. Seeded from the user's saved default,
  // which may resolve after mount (the modal can open before settings load).
  const [availabilityMode, setAvailabilityMode] = useState<AvailabilityMode>(
    defaultAvailabilityMode ?? "all",
  );
  useEffect(() => {
    if (defaultAvailabilityMode === undefined) return;
    setAvailabilityMode(defaultAvailabilityMode);
  }, [defaultAvailabilityMode]);

  const { startDate, endDate } = useMemo(() => {
    const startDate = parseISODate(project.start_date);
    const endDate = addWeeks(startDate, project.num_weeks);
    return { startDate, endDate };
  }, [project.start_date, project.num_weeks]);

  const initialSearchPills = useMemo(
    () =>
      assignment.employees
        .map((e) => e.name)
        .filter((name): name is string => Boolean(name)),
    [assignment.employees],
  );

  const filteredEmployees = useMemo(() => {
    // Hide anyone already on the project through another role.
    const base = employees.filter((e) => !excludeEmployeeIds.has(e.id));

    if (!filterByAvailability) {
      return base;
    }

    const hoursArray = assignment.weekly_hours_allocated ?? [];
    const maxHours = Math.max(...hoursArray, 0);
    if (maxHours === 0) {
      return base;
    }

    return base.filter((employee) =>
      hasAvailabilityForDateRange(employee, startDate, endDate, maxHours, availabilityMode),
    );
  }, [
    employees,
    excludeEmployeeIds,
    filterByAvailability,
    assignment.weekly_hours_allocated,
    startDate,
    endDate,
    availabilityMode,
  ]);

  const availabilityToggle =
    assignment.weekly_hours_allocated.length > 0 ? (
      <label className="checkbox">
        <input
          type="checkbox"
          checked={filterByAvailability}
          onChange={(e) => setFilterByAvailability(e.target.checked)}
          disabled={assignment.weekly_hours_allocated.length === 0}
        />
        <span>
          Only show people that have the required hours for entire assignment
          period
        </span>
      </label>
    ) : null;

  return (
    <MakersBoard
      employees={filteredEmployees}
      loading={loading}
      error={error}
      selectedOffices={selectedOffices}
      updateEmployeeRoles={async () => {}}
      updateRolesOptimistically={() => {}}
      updateNameOptimistically={() => {}}
      updateEmployeeName={async () => {}}
      updateNicknameOptimistically={() => {}}
      updateEmployeeNickname={async () => {}}
      onEmployeeClick={onToggleEmployee}
      selectedEmployeeIds={selectedEmployeeIds}
      startDate={startDate}
      numWeeks={project.num_weeks}
      updateCapacityOptimistically={() => {}}
      updateEmployeeCapacity={async () => {}}
      initialSelectedRoles={
        assignment.required_role_id ? [assignment.required_role_id] : []
      }
      initialSearchPills={initialSearchPills}
      roleEditingDisabled
      onRefresh={refresh}
      availabilityToggle={availabilityToggle}
      availabilityMode={availabilityMode}
      onAvailabilityModeChange={setAvailabilityMode}
    />
  );
}
